/*
 * ============================================================
 * SAV_ERP — Smart Account Vision Enterprise Resource Planning
 * Copyright (c) 2026 Hatem Mohamed Abdalla.
 * All rights reserved.
 *
 * This file is part of a proprietary software product owned
 * solely by Hatem Mohamed Abdalla (UAE, +971 50 390 9781).
 * Copying, redistribution, modification, reverse engineering
 * or reuse in any form without prior written consent of the
 * copyright owner is strictly prohibited and will be
 * prosecuted under UAE Federal Law No. 38/2021 on Copyright
 * and applicable international treaties.
 *
 * Fingerprint: SAV-ERP-HMA-2026-#a7f3d0
 * ============================================================
 */
import React, { useState, useEffect } from 'react';
import { apiGet } from '../services/apiClient';
import { logError } from '../utils/errorHandler';

const SummaryDashboard = ({ language, currentUser, setCurrentPage }) => {
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState({
    bankAccounts: 4,
    receipts: 875,
    payments: 850,
    transfers: 0,
    reconciliations: 0,
    expenseClaims: 0,
    customers: 208,
    salesQuotes: 1,
    salesOrders: 1,
    salesInvoices: 595,
    creditNotes: 0,
    deliveryNotes: 0,
    suppliers: 52,
    purchaseQuotes: 1,
    purchaseOrders: 1,
    purchaseInvoices: 767,
    debitNotes: 0,
    goodsReceipts: 0,
    inventoryItems: 927,
    productionOrders: 0,
    projects: 0,
    employees: 1,
    payslips: 9,
    investments: 0,
    fixedAssets: 3,
    depreciationEntries: 0,
    capitalAccounts: 0,
    specialAccounts: 0,
    journalEntries: 0,
    folders: 0
  });

  const translations = {
    en: {
      title: 'Business Summary',
      subtitle: 'Overview of all business activities',
      lastUpdated: 'Last updated',
      bankingCash: 'Banking & Cash',
      sales: 'Sales',
      purchases: 'Purchases',
      inventory: 'Inventory & Production',
      hr: 'HR & Projects',
      assets: 'Assets & Investments',
      accounting: 'Accounting',
      system: 'System',
      // Banking & Cash
      bankAccounts: 'Bank and Cash Accounts',
      receipts: 'Receipts',
      payments: 'Payments',
      transfers: 'Inter Account Transfers',
      reconciliations: 'Bank Reconciliations',
      expenseClaims: 'Expense Claims',
      // Sales
      customers: 'Customers',
      salesQuotes: 'Sales Quotes',
      salesOrders: 'Sales Orders',
      salesInvoices: 'Sales Invoices',
      creditNotes: 'Credit Notes',
      deliveryNotes: 'Delivery Notes',
      // Purchases
      suppliers: 'Suppliers',
      purchaseQuotes: 'Purchase Quotes',
      purchaseOrders: 'Purchase Orders',
      purchaseInvoices: 'Purchase Invoices',
      debitNotes: 'Debit Notes',
      goodsReceipts: 'Goods Receipts',
      // Inventory & Production
      inventoryItems: 'Inventory Items',
      productionOrders: 'Production Orders',
      // HR & Projects
      projects: 'Projects',
      employees: 'Employees',
      payslips: 'Payslips',
      // Assets & Investments
      investments: 'Investments',
      fixedAssets: 'Fixed Assets',
      depreciationEntries: 'Depreciation Entries',
      // Accounting
      capitalAccounts: 'Capital Accounts',
      specialAccounts: 'Special Accounts',
      journalEntries: 'Journal Entries',
      // System
      folders: 'Folders',
      viewAll: 'View All',
      addNew: 'Add New',
      quickAccess: 'Quick Access',
      recentActivity: 'Recent Activity'
    },
    ar: {
      title: 'ملخص الأعمال',
      subtitle: 'نظرة عامة على جميع أنشطة الأعمال',
      lastUpdated: 'آخر تحديث',
      bankingCash: 'البنوك والنقدية',
      sales: 'المبيعات',
      purchases: 'المشتريات',
      inventory: 'المخزون والإنتاج',
      hr: 'الموارد البشرية والمشاريع',
      assets: 'الأصول والاستثمارات',
      accounting: 'المحاسبة',
      system: 'النظام',
      // Banking & Cash
      bankAccounts: 'حسابات البنك والنقد',
      receipts: 'المقبوضات',
      payments: 'المدفوعات',
      transfers: 'تحويلات بين الحسابات',
      reconciliations: 'مطابقات حساب البنك',
      expenseClaims: 'عهدة المصروفات النقدية',
      // Sales
      customers: 'العملاء',
      salesQuotes: 'عروض أسعار البيع',
      salesOrders: 'أوامر البيع',
      salesInvoices: 'فواتير البيع',
      creditNotes: 'الإشعارات الدائنة',
      deliveryNotes: 'سندات التسليم',
      // Purchases
      suppliers: 'الموردون',
      purchaseQuotes: 'عروض أسعار الشراء',
      purchaseOrders: 'أوامر الشراء',
      purchaseInvoices: 'فواتير الشراء',
      debitNotes: 'الإشعارات المدينة',
      goodsReceipts: 'سندات استلام البضائع',
      // Inventory & Production
      inventoryItems: 'أصناف المخزون',
      productionOrders: 'أوامر الإنتاج',
      // HR & Projects
      projects: 'المشاريع',
      employees: 'الموظفون',
      payslips: 'قسائم الرواتب',
      // Assets & Investments
      investments: 'الاستثمارات',
      fixedAssets: 'الأصول الثابتة',
      depreciationEntries: 'قيود إهلاك الأصول الثابتة',
      // Accounting
      capitalAccounts: 'حسابات رأس المال',
      specialAccounts: 'حسابات خاصة',
      journalEntries: 'القيود المحاسبية',
      // System
      folders: 'المجلدات',
      viewAll: 'عرض الكل',
      addNew: 'إضافة جديد',
      quickAccess: 'وصول سريع',
      recentActivity: 'النشاط الحديث'
    }
  };

  const t = (key) => translations[language]?.[key] || key;

  useEffect(() => {
    loadSummaryData();
  }, []);

  const loadSummaryData = async () => {
    try {
      setLoading(true);
      // Load actual counts from different endpoints
      const [
        customersResponse,
        suppliersResponse,
        salesInvoicesResponse,
        purchaseInvoicesResponse,
        receiptsResponse,
        paymentsResponse,
        journalEntriesResponse,
        bankAccountsResponse,
        inventoryResponse,
        creditNotesResponse,
        debitNotesResponse,
        salesQuotesResponse,
        purchaseOrdersResponse,
      ] = await Promise.allSettled([
        apiGet('/customers'),
        apiGet('/suppliers'),
        apiGet('/sales-invoices'),
        apiGet('/purchase-invoices'),
        apiGet('/receipts'),
        apiGet('/payments'),
        apiGet('/journal-entries'),
        apiGet('/bank-accounts'),
        apiGet('/inventory/items'),
        apiGet('/credit-notes'),
        apiGet('/debit-notes'),
        apiGet('/sales-quotes'),
        apiGet('/purchase-orders'),
      ]);

      // Update summary data with actual counts
      const newSummaryData = { ...summaryData };
      
      if (customersResponse.status === 'fulfilled') {
        newSummaryData.customers = (Array.isArray(customersResponse.value) ? customersResponse.value.length : 0) || 0;
      }
      
      if (suppliersResponse.status === 'fulfilled') {
        newSummaryData.suppliers = (Array.isArray(suppliersResponse.value) ? suppliersResponse.value.length : 0) || 0;
      }
      
      if (salesInvoicesResponse.status === 'fulfilled') {
        newSummaryData.salesInvoices = (Array.isArray(salesInvoicesResponse.value) ? salesInvoicesResponse.value.length : 0) || 0;
      }
      
      if (purchaseInvoicesResponse.status === 'fulfilled') {
        newSummaryData.purchaseInvoices = (Array.isArray(purchaseInvoicesResponse.value) ? purchaseInvoicesResponse.value.length : 0) || 0;
      }
      
      if (receiptsResponse.status === 'fulfilled') {
        newSummaryData.receipts = (Array.isArray(receiptsResponse.value) ? receiptsResponse.value.length : 0) || 0;
      }
      
      if (paymentsResponse.status === 'fulfilled') {
        newSummaryData.payments = (Array.isArray(paymentsResponse.value) ? paymentsResponse.value.length : 0) || 0;
      }
      
      if (journalEntriesResponse.status === 'fulfilled') {
        newSummaryData.journalEntries = (Array.isArray(journalEntriesResponse.value) ? journalEntriesResponse.value.length : 0) || 0;
      }
      
      if (bankAccountsResponse.status === 'fulfilled') {
        newSummaryData.bankAccounts = (Array.isArray(bankAccountsResponse.value) ? bankAccountsResponse.value.length : 0) || 0;
      }
      
      if (inventoryResponse.status === 'fulfilled') {
        newSummaryData.inventoryItems = (Array.isArray(inventoryResponse.value) ? inventoryResponse.value.length : 0) || 0;
      }
      
      if (creditNotesResponse.status === 'fulfilled') {
        newSummaryData.creditNotes = (Array.isArray(creditNotesResponse.value) ? creditNotesResponse.value.length : 0) || 0;
      }
      
      if (debitNotesResponse.status === 'fulfilled') {
        newSummaryData.debitNotes = (Array.isArray(debitNotesResponse.value) ? debitNotesResponse.value.length : 0) || 0;
      }
      
      if (salesQuotesResponse.status === 'fulfilled') {
        newSummaryData.salesQuotes = (Array.isArray(salesQuotesResponse.value) ? salesQuotesResponse.value.length : 0) || 0;
      }
      
      if (purchaseOrdersResponse.status === 'fulfilled') {
        newSummaryData.purchaseOrders = (Array.isArray(purchaseOrdersResponse.value) ? purchaseOrdersResponse.value.length : 0) || 0;
      }

      setSummaryData(newSummaryData);
    } catch (error) {
      logError(error, 'SummaryDashboard.loadSummaryData');
    } finally {
      setLoading(false);
    }
  };

  const summaryGroups = [
    {
      title: t('bankingCash'),
      icon: '🏦',
      color: 'blue',
      items: [
        { key: 'bankAccounts', label: t('bankAccounts'), count: summaryData.bankAccounts, route: 'bank-accounts' },
        { key: 'receipts', label: t('receipts'), count: summaryData.receipts, route: 'receipts' },
        { key: 'payments', label: t('payments'), count: summaryData.payments, route: 'payments' },
        { key: 'transfers', label: t('transfers'), count: summaryData.transfers, route: 'transfers' },
        { key: 'reconciliations', label: t('reconciliations'), count: summaryData.reconciliations, route: 'reconciliation' },
        { key: 'expenseClaims', label: t('expenseClaims'), count: summaryData.expenseClaims, route: 'expense-claims' }
      ]
    },
    {
      title: t('sales'),
      icon: '💰',
      color: 'green',
      items: [
        { key: 'customers', label: t('customers'), count: summaryData.customers, route: 'customers' },
        { key: 'salesQuotes', label: t('salesQuotes'), count: summaryData.salesQuotes, route: 'sales-quotes' },
        { key: 'salesOrders', label: t('salesOrders'), count: summaryData.salesOrders, route: 'sales-orders' },
        { key: 'salesInvoices', label: t('salesInvoices'), count: summaryData.salesInvoices, route: 'sales-invoices' },
        { key: 'creditNotes', label: t('creditNotes'), count: summaryData.creditNotes, route: 'credit-notes' },
        { key: 'deliveryNotes', label: t('deliveryNotes'), count: summaryData.deliveryNotes, route: 'delivery-notes' }
      ]
    },
    {
      title: t('purchases'),
      icon: '🛒',
      color: 'orange',
      items: [
        { key: 'suppliers', label: t('suppliers'), count: summaryData.suppliers, route: 'suppliers' },
        { key: 'purchaseQuotes', label: t('purchaseQuotes'), count: summaryData.purchaseQuotes, route: 'purchase-quotes' },
        { key: 'purchaseOrders', label: t('purchaseOrders'), count: summaryData.purchaseOrders, route: 'purchase-orders' },
        { key: 'purchaseInvoices', label: t('purchaseInvoices'), count: summaryData.purchaseInvoices, route: 'purchase-invoices' },
        { key: 'debitNotes', label: t('debitNotes'), count: summaryData.debitNotes, route: 'debit-notes' },
        { key: 'goodsReceipts', label: t('goodsReceipts'), count: summaryData.goodsReceipts, route: 'goods-receipts' }
      ]
    },
    {
      title: t('inventory'),
      icon: '📦',
      color: 'purple',
      items: [
        { key: 'inventoryItems', label: t('inventoryItems'), count: summaryData.inventoryItems, route: 'inventory' },
        { key: 'productionOrders', label: t('productionOrders'), count: summaryData.productionOrders, route: 'work-orders' }
      ]
    },
    {
      title: t('hr'),
      icon: '👥',
      color: 'indigo',
      items: [
        { key: 'projects', label: t('projects'), count: summaryData.projects, route: 'projects' },
        { key: 'employees', label: t('employees'), count: summaryData.employees, route: 'employees' },
        { key: 'payslips', label: t('payslips'), count: summaryData.payslips, route: 'payroll-vouchers' }
      ]
    },
    {
      title: t('assets'),
      icon: '💎',
      color: 'pink',
      items: [
        { key: 'investments', label: t('investments'), count: summaryData.investments, route: 'investments' },
        { key: 'fixedAssets', label: t('fixedAssets'), count: summaryData.fixedAssets, route: 'assets' },
        { key: 'depreciationEntries', label: t('depreciationEntries'), count: summaryData.depreciationEntries, route: 'assets' }
      ]
    },
    {
      title: t('accounting'),
      icon: '📚',
      color: 'teal',
      items: [
        { key: 'capitalAccounts', label: t('capitalAccounts'), count: summaryData.capitalAccounts, route: 'chart-of-accounts' },
        { key: 'specialAccounts', label: t('specialAccounts'), count: summaryData.specialAccounts, route: 'chart-of-accounts' },
        { key: 'journalEntries', label: t('journalEntries'), count: summaryData.journalEntries, route: 'journal-entries' }
      ]
    },
    {
      title: t('system'),
      icon: '⚙️',
      color: 'gray',
      items: [
        { key: 'folders', label: t('folders'), count: summaryData.folders, route: 'backup' }
      ]
    }
  ];

  const handleNavigate = (route) => {
    if (setCurrentPage) {
      setCurrentPage(route);
      return;
    }
    window.dispatchEvent(new CustomEvent('navigateTo', { detail: route }));
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100',
      green: 'border-green-200 bg-green-50 text-green-800 hover:bg-green-100',
      orange: 'border-orange-200 bg-orange-50 text-orange-800 hover:bg-orange-100',
      purple: 'border-purple-200 bg-purple-50 text-purple-800 hover:bg-purple-100',
      indigo: 'border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100',
      pink: 'border-pink-200 bg-pink-50 text-pink-800 hover:bg-pink-100',
      teal: 'border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100',
      gray: 'border-gray-200 bg-gray-50 text-gray-800 hover:bg-gray-100'
    };
    return colors[color] || colors.gray;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg border p-6">
                <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
                <div className="space-y-3">
                  {[...Array(4)].map((_, j) => (
                    <div key={j} className="h-4 bg-gray-200 rounded"></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-gray-900">{t('title')}</h1>
          <div className="text-sm text-gray-500">
            {t('lastUpdated')}: {new Date().toLocaleString(language === 'ar' ? 'ar-AE' : 'en-US')}
          </div>
        </div>
        <p className="text-gray-600">{t('subtitle')}</p>
      </div>

      {/* Summary Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
        {summaryGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Group Header */}
            <div className="flex items-center mb-4">
              <span className="text-2xl mr-3">{group.icon}</span>
              <h2 className="text-lg font-semibold text-gray-900">{group.title}</h2>
            </div>

            {/* Group Items */}
            <div className="space-y-2">
              {group.items.map((item) => (
                <div
                  key={item.key}
                  onClick={() => handleNavigate(item.route)}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${getColorClasses(group.color)}`}
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm">{item.label}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">
                      {item.count.toLocaleString(language === 'ar' ? 'ar-AE' : 'en-US')}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Group Actions */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex space-x-2">
                <button
                  onClick={() => handleNavigate(group.items[0]?.route)}
                  className="flex-1 text-sm bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors"
                >
                  {t('viewAll')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('quickAccess')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[
            { label: t('salesInvoices'), route: 'sales-invoices', icon: '🧾', color: 'bg-green-100 text-green-800' },
            { label: t('purchaseInvoices'), route: 'purchase-invoices', icon: '📄', color: 'bg-orange-100 text-orange-800' },
            { label: t('customers'), route: 'customers', icon: '👥', color: 'bg-blue-100 text-blue-800' },
            { label: t('suppliers'), route: 'suppliers', icon: '🏭', color: 'bg-purple-100 text-purple-800' },
            { label: t('receipts'), route: 'receipts', icon: '💰', color: 'bg-indigo-100 text-indigo-800' },
            { label: t('payments'), route: 'payments', icon: '💳', color: 'bg-pink-100 text-pink-800' }
          ].map((action, index) => (
            <button
              key={index}
              onClick={() => handleNavigate(action.route)}
              className={`p-4 rounded-lg border border-gray-200 hover:shadow-md transition-all ${action.color}`}
            >
              <div className="text-2xl mb-2">{action.icon}</div>
              <div className="text-sm font-medium">{action.label}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SummaryDashboard;